import { Type } from "class-transformer";
import { addMinutes, areIntervalsOverlapping, startOfDay } from "date-fns";
import { AbstractJSONModel } from "./master-model";
import { IsInt, IsDate, Min, IsOptional, IsNotEmpty, IsEnum } from "class-validator";

import store from "../store";
import { eSource, eTarget } from "./dutyEnums";

export { eSource, eTarget };

export interface IDuty {
  id: number;
  name: string;
  duration_override: number | null;
  source: eSource;
  target: eTarget;
  employee_id: number;
  created_at: Date;
  updated_at: Date;
  start_date: Date;
  planned_start_time: number | null;
  planned_end_time: number | null;
  actual_start_time: number | null;
  actual_end_time: number | null;
  conversion_factor: number;
  bell_time_id: number | null;
  school_class_id: number | null;
  school_time_schedule_id: number | null;
  work_hour_pool_id: number | null;
  event_id: number | null;
  has_work_hour_pool_consumption: boolean;
  department_id: number | null;
}

export interface IDutyPost {
  name: string;
  source: eSource;
  target: eTarget;
  employee_id: number;
  start_date: Date;
  actual_start_time: number | null;
  actual_end_time: number | null;
  planned_start_time: number | null;
  planned_end_time: number | null;
  work_hour_pool_id: number | null;
  duration_override: number | null;
  department_id: number | null;
}

export interface IDutyPut {
  name: string;
  source: eSource;
  target: eTarget;
  start_date: Date;
  actual_start_time: number | null;
  actual_end_time: number | null;
  work_hour_pool_id: number | null;
  duration_override: number | null;
  department_id: number | null;
}

export class Duty extends AbstractJSONModel implements IDuty {
  public id = -1;
  public name = "";
  @IsOptional()
  @IsInt()
  public duration_override: number | null = null;
  @IsNotEmpty()
  @IsEnum(eSource)
  public source: eSource = 0;
  @IsNotEmpty()
  @IsEnum(eTarget)
  public target: eTarget = 0;
  public employee_id = 0;
  @IsDate()
  @Type(() => Date)
  public created_at: Date = new Date();
  @IsDate()
  @Type(() => Date)
  public updated_at: Date = new Date();
  @IsDate()
  @Type(() => Date)
  public start_date: Date = new Date();
  public planned_start_time: number | null = null;
  public planned_end_time: number | null = null;
  public actual_start_time: number | null = null;
  public actual_end_time: number | null = null;
  @Min(0)
  public conversion_factor = 0;
  @IsOptional()
  @IsInt()
  public bell_time_id: number | null = null;
  @IsOptional()
  @IsInt()
  public school_class_id: number | null = null;
  @IsOptional()
  @IsInt()
  public school_time_schedule_id: number | null = null;
  @IsInt()
  @IsOptional()
  public work_hour_pool_id: number | null = null;

  public changeRequestId: number | null = null;
  public event_id: number | null = null;
  public has_work_hour_pool_consumption: boolean = false;
  @IsOptional()
  @IsInt()
  public department_id: number | null = null;
  public get start_time(): number {
    return this.actual_start_time ? this.actual_start_time : this.planned_start_time ?? 0;
  }

  public get end_time(): number {
    return this.actual_end_time ? this.actual_end_time : this.planned_end_time ?? 0;
  }

  equals(other: IDuty): boolean {
    return other.id === this.id;
  }

  public duration(is_zero_by_absence = false): number {
    if (is_zero_by_absence) {
      return 0.0;
    } else if (this.duration_override) {
      return this.duration_override;
    }
    const unconverted_duration = this.end_time - this.start_time;
    return unconverted_duration * this.conversion_factor;
  }

  public get startDateTime(): Date {
    return addMinutes(startOfDay(this.start_date), this.start_time);
  }

  public get endDateTime(): Date {
    return addMinutes(startOfDay(this.start_date), this.end_time);
  }

  public get isOpenDuty(): boolean {
    const a: { open: number; close: number } = store.getters["institutions/openCloseTimeDate"](
      this.start_date
    );
    // Skal være til rådighed 60 minutter efter åbnetid
    const isOpeningDuty =
      this.start_time <= a.open &&
      this.end_time >= a.open + 60 &&
      !this.duration_override &&
      this.source !== eSource.InstitutionCalendar;
    if (!isOpeningDuty) {
      return false;
    }
    let dayEvents: Duty[] = [];

    dayEvents = store.getters["duties/getEventsOnDayPeriode"](
      this.startDateTime,
      this.endDateTime,
      this.employee_id
    );

    const openingTimeStart = addMinutes(startOfDay(this.start_date), a.open);
    const openingTimeEnd = addMinutes(startOfDay(this.start_date), a.open + 60);

    return (
      dayEvents.filter((e) =>
        areIntervalsOverlapping(
          { start: openingTimeStart, end: openingTimeEnd },
          {
            start: e.startDateTime,
            end: e.endDateTime
          }
        )
      ).length == 0
    );
  }

  public get isCloseDuty(): boolean {
    const a: { open: number; close: number } = store.getters["institutions/openCloseTimeDate"](
      this.start_date
    );
    // Skal være til rådighed 60 minutter før lukketid
    const isClosingDuty =
      this.start_time <= a.close - 60 &&
      this.end_time >= a.close &&
      !this.duration_override &&
      this.source !== eSource.InstitutionCalendar;
    if (!isClosingDuty) {
      return false;
    }
    let dayEvents: Duty[] = [];
    dayEvents = store.getters["duties/getEventsOnDayPeriode"](
      this.startDateTime,
      this.endDateTime,
      this.employee_id
    );

    const closingTimeStart = addMinutes(startOfDay(this.start_date), a.close - 60);
    const closingTimeEnd = addMinutes(startOfDay(this.start_date), a.close);

    return (
      dayEvents.filter((e) =>
        areIntervalsOverlapping(
          { start: closingTimeStart, end: closingTimeEnd },
          {
            start: e.startDateTime,
            end: e.endDateTime
          }
        )
      ).length == 0
    );
  }

  public isPlannedTime(start_time: number, end_time: number): boolean {
    return this.planned_start_time === start_time && this.planned_end_time === end_time;
  }

  public resetToPlannedTime(start_time: number, end_time: number) {
    this.planned_start_time = start_time;
    this.planned_end_time = end_time;
    this.actual_start_time = null;
    this.actual_end_time = null;
  }

  public convertToPutObject(): IDutyPut {
    const d: IDutyPut = Object.assign({});
    d.actual_end_time = this.actual_end_time;
    d.actual_start_time = this.actual_start_time;
    d.name = this.name;
    d.start_date = this.start_date;
    d.source = this.source;
    d.target = this.target;
    // duties with pool time cannot be edited directly on the frontend, is done as
    // part of editing events, and then duties is updated on the backend.
    d.work_hour_pool_id = null;
    d.duration_override = this.duration_override;
    d.department_id = this.department_id;
    return d;
  }

  public convertToPostObject(): IDutyPost {
    const d: IDutyPost = Object.assign({});

    d.name = this.name;
    d.start_date = this.start_date;
    // duties created on frontend is allways Manual source and
    // SupervisionTime target, so far.
    d.source = eSource.Manual;
    d.target = eTarget.SupervisionTime;

    d.employee_id = this.employee_id;

    // duties created on frontened is allways actual time
    d.actual_start_time = this.actual_start_time;
    d.actual_end_time = this.actual_end_time;

    // duties created on frontend cannot have planned times
    d.planned_start_time = null;
    d.planned_end_time = null;

    d.duration_override = this.duration_override;

    // duties created on frontend cannot have pool time.
    // duties with pool time comes from events (aktiviteter), and is created on the
    // backend as part of creating an event
    d.work_hour_pool_id = null;

    d.department_id = this.department_id;

    return d;
  }
}
